/**
 * THE SHOP'S OWN WORDS FOR THE WORK IT DOES.
 *
 * Anthony sent a service reference built from the real book — 1,948 jobs
 * boiled down to 158 terms — and asked for it to "autofill or auto populate
 * one of these when someone goes to type in a part along with any saved
 * parts or labor items we've done."
 *
 * ─── WHY IT MATTERS MORE THAN IT LOOKS ─────────────────────────────────
 *
 * The book already proves the cost of not having this. The same job is
 * written "front pads and rotors", "front breaks", "brake job" and
 * "brakes", so nothing can be counted, priced from history, or found by
 * searching. 667 jobs say Diagnostic and a handful say "disgnostic".
 *
 * It is a SUGGESTION, never a cage. Anybody can still type what the job
 * actually is; what this does is make the right words the easiest ones to
 * reach, which is the only kind of vocabulary a busy shop ever adopts.
 *
 * ─── HOW IT RANKS ──────────────────────────────────────────────────────
 *
 * What this shop has DONE beats what it could do. A part fitted before
 * carries a real price and a real part number, so it wins over a term from
 * the reference every time. Inside the reference, the words the book uses
 * most come first — `seen` is a count of real jobs, not an opinion.
 *
 * A match on the start of a word beats a match in the middle, so typing
 * "bra" offers Brake Pads before Sway Bar Link.
 */

export type ServiceTerm = {
  term: string;
  section: string;
  aliases: string[];
  seen: number;
};

/** Lowercased, punctuation flattened, so "a/c" and "ac" are the same word. */
const flat = (s: unknown) =>
  String(s ?? "").toLowerCase().replace(/[^a-z0-9]+/g, " ").trim();

/**
 * WHAT THE SHOP MEANT TO TYPE.
 *
 * Every entry came off the real book, so this is not spellchecking, it is
 * recognizing twenty specific mistakes this shop has actually made. Applied
 * on the whole string and on each word, because "front breaks" and "breaks"
 * are both in there and only one of them is a whole entry.
 */
export function fixSpelling(text: string, fixes: Map<string, string>): string {
  const whole = fixes.get(flat(text));
  if (whole) return whole;
  const words = String(text ?? "").split(/(\s+)/);
  let touched = false;
  const out = words.map((w) => {
    const hit = fixes.get(flat(w));
    if (hit && flat(w)) { touched = true; return hit; }
    return w;
  });
  return touched ? out.join("") : text;
}

export type Suggestion = {
  label: string;
  /** Where it came from, so the row can say. */
  from: "fitted" | "quoted" | "reference";
  hint?: string;
  /** Carried through when the shop has fitted this before. */
  cost?: number | null;
  partNumber?: string | null;
  hours?: number | null;
};

/**
 * WHAT TO OFFER FOR WHAT HAS BEEN TYPED SO FAR.
 *
 * Nothing at all under two characters: a list that appears on the first
 * keystroke is a list that covers the field somebody is trying to type in.
 */
export function suggest(
  typed: string,
  opts: { terms: ServiceTerm[]; done?: Suggestion[]; limit?: number } = { terms: [] },
): Suggestion[] {
  const q = flat(typed);
  if (q.length < 2) return [];
  const limit = opts.limit ?? 8;

  const score = (hay: string): number => {
    const h = flat(hay);
    if (!h) return 0;
    if (h === q) return 100;
    if (h.startsWith(q)) return 60;
    // A match at the start of any WORD, which is how people search.
    if (new RegExp(`\\b${q.replace(/[.*+?^${}()|[\\]\\\\]/g, "\\\\$&")}`).test(h)) return 40;
    if (h.includes(q)) return 15;
    return 0;
  };

  const out: Array<Suggestion & { s: number }> = [];
  const seen = new Set<string>();

  // What the shop has actually done, first. A real price beats a good word.
  for (const d of opts.done ?? []) {
    const s = score(d.label);
    if (!s) continue;
    const key = flat(d.label);
    if (seen.has(key)) continue;
    seen.add(key);
    out.push({ ...d, s: s + 25 });
  }

  for (const t of opts.terms) {
    const best = Math.max(score(t.term), ...t.aliases.map(score));
    if (!best) continue;
    const key = flat(t.term);
    if (seen.has(key)) continue;
    seen.add(key);
    out.push({
      label: t.term, from: "reference", hint: t.section,
      // The book's own usage breaks ties, so the words this shop says most
      // come first. Capped so a 667-job term cannot outrank an exact match.
      s: best + Math.min(t.seen, 20) / 4,
    });
  }

  return out.sort((a, b) => b.s - a.s).slice(0, limit)
    .map(({ s: _s, ...rest }) => rest);
}
